import {
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Chip,
  Box,
  Card,
  CardContent,
  Divider,
  alpha,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { makeStyles } from "@mui/styles";
import {
  getExportProductById,
  getProductionsForSales,
} from "../../../services/Export";
import { getCurrentProductPricing } from "../../../services/ExportPrice";
import InputField from "../../../FormComponents/InputField";
import ProductionWritableTable from "../../../components/ProductionWritableTable";
import ProfitCard from "../../../components/ProfitCard";

const useStyles = makeStyles({
  categoryContainer: {
    padding: "10px",
  },
  categoryTag: {
    marginRight: "10px",
  },
});

function SaleBillItemForm({
  productRow,
  setProductRows,
  handleOpenProductProcess,
  billType,
}) {
  const classes = useStyles();

  const [value, setValue] = useState({
    _sellingQty: 0.0,
    _sellingPricePerUnit: 0.0,
    _discountType: "PERCENTAGE", // "PERCENTAGE" or "AMOUNT"
    _discountValue: 0.0,
  });

  const [selectedProductData, setSelectedProductData] = useState(null);
  const [rows, setRows] = useState([]);
  const [profit, setProfit] = useState(0.0);
  const [profitMargin, setProfitMargin] = useState(0.0);
  const [productionCost, setProductionCost] = useState(0.0);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0.0);

  const [writeable_page, set_writeable_Page] = useState(0);
  const [writeable_rowsPerPage, set_writeable_RowsPerPage] = useState(100);

  const [error, setError] = useState({
    _product: "",
    _sellingQty: "",
  });

  // Load product data on mount
  useEffect(() => {
    loadProductData();
  }, []);

  // Load production data when selling qty changes
  useEffect(() => {
    if (value._sellingQty > 0) {
      setError({
        ...error,
        _product: "",
      });
      loadProductionData();
    } else {
      setRows([]);
      setProductionCost(0.0);
    }
  }, [value._sellingQty]);

  // Recalculate profit when production cost or unit price changes
  useEffect(() => {
    if (value._sellingQty > 0 && productionCost > 0) {
      calculateProfitFromPrice(value._sellingPricePerUnit);
    }
  }, [productionCost]);

  // Recalculate discount when discount value or type changes
  useEffect(() => {
    calculateDiscount();
  }, [
    value._discountValue,
    value._discountType,
    value._sellingQty,
    value._sellingPricePerUnit,
  ]);

  const loadProductData = async () => {
    // Set existing values from productRow if editing
    if (productRow.sellingQty > 0) {
      setValue({
        _sellingQty: productRow.sellingQty,
        _sellingPricePerUnit: productRow.unitPrice || 0,
        _discountType: productRow.discountType || "PERCENTAGE",
        _discountValue: productRow.discountValue || 0,
      });
      setProfitMargin(productRow.profitMargin || 0);
      setProfit(productRow.totalProfit || 0);
      setProductionCost(productRow.totalCost || 0);
      setDiscountAmount(productRow.discountAmount || 0);

      if (productRow.batches && productRow.batches.length > 0) {
        setRows(productRow.batches);
      }
    }

    // Fetch product details
    try {
      const productResponse = await getExportProductById(productRow.productId);

      if (productResponse === "Something went wrong...") {
        setError({
          ...error,
          _product: "Unable to fetch product data..",
        });
        return;
      }

      setSelectedProductData(productResponse);

      // Fetch pricing based on bill type if not already set
      if (productRow.sellingQty === 0 || !productRow.unitPrice) {
        await fetchProductPricing(productResponse.barcode);
      }
    } catch (error) {
      console.error("Error loading product:", error);
      setError({
        ...error,
        _product: "Unable to fetch product data..",
      });
    }
  };

  const fetchProductPricing = async (barcode) => {
    setIsLoadingPrice(true);
    try {
      console.log(`Fetching ${billType} pricing for barcode: ${barcode}`);

      const pricingData = await getCurrentProductPricing(barcode);

      if (pricingData && pricingData !== "Something went wrong...") {
        // Set price based on bill type
        let unitPrice = 0;
        if (billType === "RETAIL" && pricingData.currentRetailPrice) {
          unitPrice = pricingData.currentRetailPrice;
        } else if (billType === "WHOLESALE" && pricingData.currentWholesalePrice) {
          unitPrice = pricingData.currentWholesalePrice;
        } else {
          unitPrice = pricingData.retailPrice || pricingData.wholesalePrice || 0;
        }

        setValue((prev) => ({
          ...prev,
          _sellingPricePerUnit: unitPrice,
        }));
      }
    } catch (error) {
      console.error("Error fetching product pricing:", error);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const loadProductionData = async () => {
    if (value._sellingQty > 0 && productRow.productId) {
      try {
        let productionSet = await getProductionsForSales(
          productRow.productId,
          value._sellingQty,
        );

        if (productionSet.length === 0) {
          setRows([]);
          setError({
            ...error,
            _product: "No sufficient productions found for this product",
          });
          setProductionCost(0.0);
          return;
        }

        // Sort by production date (oldest first - FIFO)
        productionSet.sort((a, b) => {
          return new Date(a.productionDate) - new Date(b.productionDate);
        });

        const selectedArray = [];
        let requiredQty = parseFloat(value._sellingQty);
        let costVal = 0.0;

        for (let x = 0; x < productionSet.length; x++) {
          const production = productionSet[x];

          let availableQty =
            parseFloat(production.productionQty) -
            parseFloat(production.soldQuantity);

          let sellingQty = 0;
          if (requiredQty > availableQty) {
            sellingQty = availableQty;
            requiredQty = parseFloat((requiredQty - availableQty).toFixed(4));
          } else {
            sellingQty = requiredQty;
            requiredQty = 0;
          }

          const prodQty = parseFloat(production.productionQty) || 0;
          const costPerUnit = prodQty > 0
            ? parseFloat((parseFloat(production.totalCost) / prodQty).toFixed(4))
            : 0;
          const batchCost = parseFloat((costPerUnit * sellingQty).toFixed(2));
          costVal = parseFloat((costVal + batchCost).toFixed(2));

          selectedArray.push(
            createDataForProductionTable(
              production.productionDate,
              production.productionId.toString(),
              availableQty,
              production.totalItemCost,
              production.labourCost,
              production.electricityCost,
              production.transportCost,
              production.otherCost,
              production.totalCost,
              sellingQty,
              production.productionId,
            ),
          );

          if (requiredQty <= 0) break;
        }

        setProductionCost(costVal);
        setRows(selectedArray);

        // Auto-calculate profit with current price
        if (value._sellingPricePerUnit > 0) {
          calculateProfitFromPrice(value._sellingPricePerUnit);
        }
      } catch (error) {
        console.error("Error loading production data:", error);
        setError({
          ...error,
          _product: "Failed to load production data",
        });
      }
    } else {
      setRows([]);
      setProductionCost(0.0);
    }
  };

  const calculateDiscount = () => {
    if (!value._sellingQty || !value._sellingPricePerUnit) {
      setDiscountAmount(0);
      return;
    }

    const subtotal = value._sellingPricePerUnit * value._sellingQty;
    let discount = 0;

    if (value._discountType === "PERCENTAGE") {
      discount = (subtotal * parseFloat(value._discountValue || 0)) / 100;
    } else {
      discount = parseFloat(value._discountValue || 0);
    }

    setDiscountAmount(parseFloat(discount.toFixed(2)));

    // Recalculate profit with discount
    if (productionCost > 0) {
      const totalAfterDiscount = subtotal - discount;
      const totalProfit = totalAfterDiscount - productionCost;
      setProfit(parseFloat(totalProfit.toFixed(2)));

      if (totalAfterDiscount > 0) {
        const profitMarginVal = (totalProfit / totalAfterDiscount) * 100;
        setProfitMargin(parseFloat(profitMarginVal.toFixed(2)));
      } else {
        setProfitMargin(0);
      }
    }
  };

  const calculateProfitFromPrice = (unitPrice) => {
    if (!value._sellingQty || value._sellingQty <= 0 || !productionCost) return;

    const priceValue = parseFloat(unitPrice);
    if (isNaN(priceValue)) return;

    const subtotal = priceValue * value._sellingQty;
    const totalAfterDiscount = subtotal - discountAmount;
    const totalProfit = totalAfterDiscount - productionCost;

    if (!isNaN(totalProfit)) {
      setProfit(parseFloat(totalProfit.toFixed(2)));
    }

    if (totalAfterDiscount > 0) {
      const profitMarginVal = (totalProfit / totalAfterDiscount) * 100;
      if (!isNaN(profitMarginVal)) {
        setProfitMargin(parseFloat(profitMarginVal.toFixed(2)));
      }
    } else {
      setProfitMargin(0);
    }
  };

  const handleProfit = (val) => {
    calculateProfitFromPrice(val);
  };

  const handleProfitMargin = (val) => {
    setProfitMargin(val);

    const profitMarginValue = parseFloat(val);
    if (
      isNaN(profitMarginValue) ||
      profitMarginValue >= 100 ||
      !value._sellingQty ||
      value._sellingQty <= 0 ||
      !productionCost ||
      productionCost <= 0
    )
      return;

    const sellingQty = parseFloat(value._sellingQty);
    const costPerUnit = productionCost / sellingQty;
    const unitPriceVal = parseFloat(
      (costPerUnit / (1 - profitMarginValue / 100)).toFixed(2)
    );
    const unitProfitVal = unitPriceVal - costPerUnit;

    if (!isNaN(unitProfitVal)) {
      setProfit(parseFloat((unitProfitVal * sellingQty).toFixed(2)));
      setValue({
        ...value,
        _sellingPricePerUnit: unitPriceVal,
      });
    }
  };

  const handleChange = (name, val) => {
    setValue({
      ...value,
      [name]: val,
    });
  };

  const handleDiscountTypeChange = (event, newType) => {
    if (newType !== null) {
      setValue({
        ...value,
        _discountType: newType,
        _discountValue: 0, // Reset discount value when changing type
      });
      setDiscountAmount(0);
    }
  };

  const submitValue = () => {
    // Validate
    if (!value._sellingQty || value._sellingQty <= 0) {
      setError({
        ...error,
        _sellingQty: "Please enter a valid selling quantity",
      });
      return;
    }

    if (rows.length === 0) {
      setError({
        ...error,
        _product: "No production batches available",
      });
      return;
    }

    const subtotal = value._sellingPricePerUnit * value._sellingQty;
    const totalPrice = subtotal - discountAmount;

    // Update product rows in parent component
    setProductRows((prevRows) =>
      prevRows.map((r) =>
        r.productId === productRow.productId
          ? {
              ...r,
              batches: rows,
              totalCost: parseFloat(productionCost.toFixed(2)),
              totalProfit: parseFloat(profit),
              profitMargin: parseFloat(profitMargin),
              subtotal: parseFloat(subtotal.toFixed(2)),
              discountType: value._discountType,
              discountValue: parseFloat(value._discountValue || 0),
              discountAmount: parseFloat(discountAmount.toFixed(2)),
              totalPrice: parseFloat(totalPrice.toFixed(2)),
              sellingQty: parseFloat(value._sellingQty),
              unitPrice: parseFloat(value._sellingPricePerUnit),
            }
          : r,
      ),
    );

    // Close the form
    handleOpenProductProcess();
  };

  const resetValues = () => {
    setValue({
      _sellingQty: 0.0,
      _sellingPricePerUnit: 0.0,
      _discountType: "PERCENTAGE",
      _discountValue: 0.0,
    });
    setProfitMargin(0.0);
    setProfit(0.0);
    setProductionCost(0.0);
    setDiscountAmount(0.0);
    setRows([]);
    setError({
      _product: "",
      _sellingQty: "",
    });
  };

  const productionColumns = [
    { id: "date", label: "Production Date", minWidth: 120, editable: false },
    {
      id: "batchNumber",
      label: "Production ID",
      minWidth: 120,
      editable: false,
    },
    {
      id: "availableQty",
      label: "Available Qty",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
    },
    {
      id: "saleQty",
      label: "Using Qty",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: true,
    },
    {
      id: "itemCost",
      label: "Item Cost",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "labourCost",
      label: "Labour Cost",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "electricityCost",
      label: "Electricity Cost",
      minWidth: 110,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "transportCost",
      label: "Transport Cost",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "otherCost",
      label: "Other Cost",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "totalCost",
      label: "Total Cost",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
  ];

  function createDataForProductionTable(
    date,
    batchNumber,
    availableQty,
    itemCost,
    labourCost,
    electricityCost,
    transportCost,
    otherCost,
    totalCost,
    saleQty,
    productionId,
  ) {
    return {
      date,
      batchNumber,
      availableQty,
      itemCost,
      labourCost,
      electricityCost,
      transportCost,
      otherCost,
      totalCost,
      saleQty,
      productionId,
    };
  }

  const subtotal = value._sellingPricePerUnit * value._sellingQty;
  const totalAfterDiscount = subtotal - discountAmount;

  return (
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1299,
        }}
        onClick={handleOpenProductProcess}
      />

      {/* Modal */}
      <Paper
        elevation={8}
        sx={{
          p: 3,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: "1400px",
          maxHeight: "95vh",
          overflow: "auto",
          zIndex: 1300,
          bgcolor: "background.paper",
        }}
      >
        <Grid container spacing={2}>
          {/* Header */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="600">
                  Configure Sale Item
                </Typography>
                <Chip
                  label={billType}
                  size="small"
                  color={billType === "RETAIL" ? "primary" : "secondary"}
                />
              </Box>
              <IconButton
                onClick={handleOpenProductProcess}
                aria-label="close"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
          </Grid>

          {/* Product Information */}
          {selectedProductData ? (
            <Grid item xs={12}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: alpha(
                    billType === "RETAIL" ? "#1976d2" : "#9c27b0",
                    0.05,
                  ),
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Product Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Product Name
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedProductData.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Barcode
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedProductData.barcode}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Available Quantity
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedProductData.existingQty}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        {billType} Price
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        Rs. {value._sellingPricePerUnit}
                        {isLoadingPrice && (
                          <CircularProgress size={12} sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </Grid>
                  </Grid>

                  {selectedProductData.ex_categories &&
                    selectedProductData.ex_categories.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          gutterBottom
                        >
                          Categories
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {selectedProductData.ex_categories.map((category) => (
                            <Chip
                              key={category.id}
                              label={category.category}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                </CardContent>
              </Card>
            </Grid>
          ) : error._product ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                {error._product}
              </Typography>
            </Grid>
          ) : null}

          {/* Input Fields */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Sale Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputField
                      name="_sellingQty"
                      errorMsg={error._sellingQty}
                      value={value._sellingQty}
                      onChange={(event, newInputValue) =>
                        handleChange(event, newInputValue)
                      }
                      type="number"
                      label="Selling Quantity *"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputField
                      name="_sellingPricePerUnit"
                      value={value._sellingPricePerUnit}
                      onChange={(event, newInputValue) => {
                        handleChange(event, newInputValue);
                        handleProfit(newInputValue);
                      }}
                      type="number"
                      label={`Unit Price (${billType})`}
                      isdisabled={isLoadingPrice}
                    />
                  </Grid>

                  {/* Discount Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      gutterBottom
                    >
                      Discount (Optional)
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ToggleButtonGroup
                        value={value._discountType}
                        exclusive
                        onChange={handleDiscountTypeChange}
                        size="small"
                        sx={{ height: "40px" }}
                      >
                        <ToggleButton value="PERCENTAGE">
                          <PercentIcon fontSize="small" sx={{ mr: 0.5 }} />%
                        </ToggleButton>
                        <ToggleButton value="AMOUNT">
                          <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5 }} />Rs.
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Box sx={{ flex: 1 }}>
                        <InputField
                          name="_discountValue"
                          value={value._discountValue}
                          onChange={(event, newInputValue) =>
                            handleChange(event, newInputValue)
                          }
                          type="number"
                          label={
                            value._discountType === "PERCENTAGE"
                              ? "Discount %"
                              : "Discount Amount"
                          }
                        />
                      </Box>
                    </Box>
                  </Grid>

                  {discountAmount > 0 && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: alpha("#4caf50", 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha("#4caf50", 0.3)}`,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Discount Applied
                        </Typography>
                        <Typography
                          variant="h6"
                          color="success.main"
                          fontWeight="600"
                        >
                          -${discountAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <InputField
                      onChange={(event, newInputValue) =>
                        handleProfitMargin(newInputValue)
                      }
                      name="_profitMargin"
                      value={profitMargin}
                      type="number"
                      label="Profit Margin (%)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Profit Card with Discount Info */}
          {value._sellingQty > 0 &&
            (value._sellingPricePerUnit > 0 || profitMargin > 0) &&
            rows.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ bgcolor: alpha("#4caf50", 0.05) }}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      gutterBottom
                    >
                      Sale Summary
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          Rs. {subtotal.toFixed(2)}
                        </Typography>
                      </Box>

                      {discountAmount > 0 && (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Discount (
                              {value._discountType === "PERCENTAGE"
                                ? `${value._discountValue}%`
                                : "Rs. " + value._discountValue}
                              )
                            </Typography>
                            <Typography
                              variant="body1"
                              color="error.main"
                              fontWeight="500"
                            >
                              - Rs. {discountAmount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Divider />
                        </>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body1" fontWeight="600">
                          Total Price
                        </Typography>
                        <Typography
                          variant="h6"
                          color="primary.main"
                          fontWeight="600"
                        >
                          Rs. {totalAfterDiscount.toFixed(2)}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Total Profit
                        </Typography>
                        <Typography
                          variant="h6"
                          color="success.main"
                          fontWeight="600"
                        >
                          Rs. {profit.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Profit Margin
                        </Typography>
                        <Chip
                          label={`${profitMargin.toFixed(2)}%`}
                          color="success"
                          size="small"
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Production Batches Table */}
          <Grid item xs={12}>
            {rows.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Production Batches (FIFO Order)
                </Typography>
                <ProductionWritableTable
                  columns={productionColumns}
                  rows={rows}
                  setRows={setRows}
                  page={writeable_page}
                  setPage={set_writeable_Page}
                  rowsPerPage={writeable_rowsPerPage}
                  tablePagin={false}
                  setRowsPerPage={set_writeable_RowsPerPage}
                />
              </Box>
            ) : error._product ===
              "No sufficient productions found for this product" ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  bgcolor: alpha("#f44336", 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="error">
                  {error._product}
                </Typography>
              </Box>
            ) : value._sellingQty > 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  bgcolor: alpha("#000", 0.02),
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={24} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Loading production batches...
                </Typography>
              </Box>
            ) : null}
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={resetValues} variant="outlined">
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={submitValue}
                disabled={
                  !value._sellingQty ||
                  value._sellingQty <= 0 ||
                  rows.length === 0 ||
                  isLoadingPrice
                }
                color={billType === "RETAIL" ? "primary" : "secondary"}
              >
                Done
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default SaleBillItemForm;
